import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
export function AnalysisActions({
  children,
  symbol,
  label = `Actions for ${symbol}`,
  groupLabel = `${symbol} schedule actions`,
}: {
  readonly children: ReactNode;
  readonly symbol: string;
  readonly label?: string;
  readonly groupLabel?: string;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      if (trigger === null || popover === null) return;
      const anchor = trigger.getBoundingClientRect();
      const menu = popover.getBoundingClientRect();
      const top =
        anchor.bottom + 4 + menu.height <= window.innerHeight - 8
          ? anchor.bottom + 4
          : anchor.top - menu.height - 4;
      popover.style.top = `${Math.max(8, top)}px`;
      popover.style.left = `${Math.max(8, Math.min(anchor.right - menu.width, window.innerWidth - menu.width - 8))}px`;
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="button button--quiet ai-analysis-actions-trigger"
        aria-label={label}
        popoverTarget={id}
      >
        <span aria-hidden="true">⋮</span>
      </button>
      <div
        ref={popoverRef}
        id={id}
        popover="auto"
        className="ai-analysis-actions"
        role="group"
        aria-label={groupLabel}
        onToggle={(event) => setOpen(event.currentTarget.matches(":popover-open"))}
        onClick={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest("button:not(:disabled), a[href]")
          ) {
            popoverRef.current?.hidePopover();
            triggerRef.current?.focus();
          }
        }}
      >
        {children}
      </div>
    </>
  );
}
